/**
 * Where the depth model lives, and what generation is expected to cost.
 *
 * The weights are served from blob storage rather than bundled: 47 MB in the app bundle would
 * be paid by every visitor on every deploy, where a blob with a long cache-control is fetched
 * once per device and survives releases. `services/api/upload_depth_model.py` puts it there.
 */
import type { ModelSpec } from './depthEngine'

/**
 * Which execution provider ONNX Runtime resolved to.
 *
 * Declared HERE and not in depthEngine on purpose: the opt-in prompt needs the timing table to
 * quote a cost before anything is loaded, and importing it from depthEngine made `import
 * 'onnxruntime-web'` a static dependency of the prompt -- which put 500 KB of runtime into the
 * main bundle for every visitor, measured, defeating the whole point of the dynamic import.
 * Types are erased at build time; values are not.
 */
export type Provider = 'webgpu' | 'wasm'

/** Roughly how long one frame's DEPTH takes, per provider. */
export const MS_PER_FRAME: Record<Provider, number> = {
  webgpu: 350,
  wasm: 2500,
}

const MODEL_BASE =
  (import.meta as any).env?.VITE_DEPTH_MODEL_URL || '/models/da2s_model_fp16.onnx'

/** Depth Anything V2 small, fp16 ONNX. Apache 2.0. 47.3 MB. */
export const DEPTH_MODEL: ModelSpec = {
  url: MODEL_BASE,
  // 518 = 37 ViT patches of 14 px, the size DA-V2 was exported at.
  inputSize: 518,
}

export const DEPTH_MODEL_BYTES = 47.3 * 1024 * 1024

/** ~110 ms per pair measured on desktop; a phone is several times slower. */
const MS_PER_MATCH: Record<Provider, number> = { webgpu: 300, wasm: 600 }

/**
 * A time to quote BEFORE the provider is known, so the prompt can be honest up front.
 *
 * Deliberately the pessimistic branch: the page is not cross-origin isolated (see
 * depthEngine), so a device without WebGPU falls to single-threaded WASM, and quoting the
 * WebGPU figure to someone who will get WASM is how a two-minute promise becomes ten.
 */
export function estimateSeconds(frameCount: number, provider?: Provider): number {
  const p: Provider = provider ?? (typeof navigator !== 'undefined' && 'gpu' in navigator ? 'webgpu' : 'wasm')
  return (frameCount * (MS_PER_FRAME[p] + MS_PER_MATCH[p])) / 1000
}

export function formatDuration(seconds: number): string {
  if (seconds < 90) return `${Math.max(1, Math.round(seconds))} sec`
  return `${Math.round(seconds / 60)} min`
}
